/**
 * FlexImage — Universal image renderer
 *
 * Automatically detects whether the `src` is an external URL or a local path.
 *   - External: starts with "http://" or "https://" → used as-is
 *   - Local: any other string → treated as a path relative to /public/
 *     e.g. "/images/catalogue/ring.jpg" → serves from /public/images/catalogue/ring.jpg
 *
 * Usage:
 *   <FlexImage src="https://images.unsplash.com/photo-123" alt="Necklace" className="..." />
 *   <FlexImage src="/images/catalogue/ring.jpg" alt="Ring" className="..." />
 *
 * Drop images into /public/images/ and reference them in config.json.
 */

import { useState } from "react";

const isExternalUrl = (src) =>
  typeof src === "string" && (src.startsWith("http://") || src.startsWith("https://"));

export const FlexImage = ({
  src,
  alt = "",
  className = "",
  fallback = "/images/misc/placeholder.svg",
  loading = "lazy",
  ...props
}) => {
  const [hasError, setHasError] = useState(false);

  // Resolve the image source
  const resolvedSrc = hasError
    ? fallback
    : isExternalUrl(src)
    ? src
    : src; // Local paths served from /public/ as-is by CRA

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => {
        if (!hasError) setHasError(true);
      }}
      {...props}
    />
  );
};
